import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/system';
import { createContext, ReactElement, ReactNode, useContext, useState } from 'react';

type Props = { readonly children: ReactNode };

type SidePanelProps = {
  readonly sidePanel: ReactElement;
  readonly content: ReactNode;
};

type SidePanelContextValue = {
  isOpened: boolean;
  handleOpen: (value: boolean) => void;
};

const StyledPageContainer = styled(Box)(() => {
  return {
    marginTop: '3.125rem',
    width: '100vw',
    display: 'flex',
    justifyContent: 'center',
    background: tsoPqTheme.palette.background.other.color_other_background,
  };
});

const StyledDefaultContainer = styled(Box)(() => {
  return {
    width: '76.5rem',
    display: 'flex',
    flexDirection: 'column',
  };
});
const StyledFullScreenContainer = styled(Box)(() => {
  return {
    width: '144rem',
    padding: `0 ${tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_48}`,
    display: 'flex',
    flexDirection: 'column',
  };
});

export function DefaultPageLayoutTsoPq({ children }: Props) {
  return (
    <StyledPageContainer>
      <StyledDefaultContainer>
        <Grid container spacing={3}>
          {children}
        </Grid>
      </StyledDefaultContainer>
    </StyledPageContainer>
  );
}
export function FullscreenPageLayoutTsoPq({ children }: Props) {
  return (
    <StyledPageContainer>
      <StyledFullScreenContainer>
        <Grid container spacing={3} columns={12}>
          {children}
        </Grid>
      </StyledFullScreenContainer>
    </StyledPageContainer>
  );
}

const StyledPage = styled(Box, {
  shouldForwardProp: (prop) => !['hasDrawer'].includes(prop as string),
})(() => {
  return {
    display: 'grid',
    gridTemplateColumns: '37.5rem auto',
    width: '100%',
    margin: '3.125rem 0 0 0',

    [tsoPqTheme.breakpoints.down('desktop')]: {
      gridTemplateColumns: '4rem auto',
    },
  };
});

const StyledSidePanelWrapper = styled(Box)(() => {
  return {
    width: '37.5rem',
    height: 'calc(100vh - 3.125rem)',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  };
});

const SidePanel = styled(Grid)(() => {
  return {
    width: '34.5rem',
  };
});

const StyledContentWrapper = styled(Box)(() => {
  return {
    background: tsoPqTheme.palette.background.other.color_other_background,
  };
});

const StyledContnet = styled(Grid)(() => {
  return {
    padding: `0 ${tsoPqTheme.spacingValues.pageSpacing.spacing_page_margin_24}`,
    borderLeft: '1px solid green',
    height: 'calc(100vh - 3.125rem)',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  };
});

const StyledOpenModalButton = styled(IconButtonTsoPq)(() => {
  return {
    display: 'none',
    margin: '1rem',

    [tsoPqTheme.breakpoints.down('desktop')]: {
      display: 'flex',
    },
  };
});

export const SidePanelContext = createContext<SidePanelContextValue | null>(null);

export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (!context) throw new Error('useSidePanel must be used inside SidePanelContect.Provider');
  return context;
};

export function SidePanelPageLayoutTsoPq({ sidePanel, content }: SidePanelProps) {
  const [isOpened, setIsOpened] = useState(false);

  const handleOpen = (isOpened: boolean) => {
    setIsOpened(isOpened);
  };

  const context: SidePanelContextValue = {
    isOpened,
    handleOpen,
  };

  return (
    <SidePanelContext.Provider value={context}>
      <StyledPage>
        <StyledSidePanelWrapper>
          <StyledOpenModalButton
            onClick={() => handleOpen(!isOpened)}
            buttonType="outlinedPrimary"
            sizeVariant={'s'}
            icon={faAnglesRight}
          />
          <SidePanel container columns={4} spacing={'1.5rem'}>
            {sidePanel}
          </SidePanel>
        </StyledSidePanelWrapper>
        <StyledContentWrapper>
          <StyledContnet container columns={12} spacing={'1.5rem'}>
            {content}
          </StyledContnet>
        </StyledContentWrapper>
      </StyledPage>
    </SidePanelContext.Provider>
  );
}
